import React, { useEffect, useState, useCallback } from 'react';
import { Card, Col, ProgressBar, Row, Spinner } from 'react-bootstrap';
import PuntoCercanoFiltros from './components/filters';
import { toast } from "react-toastify";
import CardMap from './components/card-map';
import { GoogleMapsProvider } from '../../../providers/GoogleMapsProvider';
import Flex from '../../../components/common/Flex';
import { iconPuntoNoEconomico, obtenerInfoView, obtenerInfoViewNormal } from '../../../components/common/google-maps-selector/GoogleMapSelector';
import { paginadoRecorridosStore } from '../../../lib/store/paginador_store';
import cargarPaginas from '../../../providers/provider_paginado';
import dayjs from 'dayjs';
import classNames from 'classnames';
import { jwtDecode } from 'jwt-decode';
import StandardProgressBar from '../../../components/common/ProgressBar/StandardProgressBar';

const PuntoCercanoReport = () => {
    const paginadorRecorridos = paginadoRecorridosStore();
    const [totalCarga, editarTotalCarga] = useState([]);
    const [isLoading, setIsLoading] = useState(false); // Estado para controlar el spinner

    const [filters, setFilters] = useState({
        fecha_inicio: new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
        fecha_fin: new Date(new Date().setHours(23, 59, 59, 999)).toISOString(),
        distancia: 500,
        uids_vehiculos: []
    });

    const [loadingComponents, setLoadingComponents] = useState({
        table: { loading: false, progress: 0, complete: false },
        vehicles: { loading: false, progress: 0, complete: false }
    });

    const calculateTotalProgress = useCallback(() => {
        const weights = {
            table: 0.4,      // ✅ Ajustar para que sume 100%
            vehicles: 0.3,   // ✅ Solo componentes que existen
            alerts: 0.3,     // ✅ Solo componentes que existen                
        };

        let totalProgress = 0;
        let totalWeight = 0;

        Object.entries(loadingComponents).forEach(([component, state]) => {
            if (state && (state.loading || state.complete)) {
                const progress = Number(state.progress);
                if (!isNaN(progress)) {
                    totalProgress += progress * weights[component];
                    totalWeight += weights[component];
                } else {
                    totalProgress += (state.complete ? 100 : 0) * weights[component];
                    totalWeight += weights[component];
                }
            }
        });

        if (totalWeight === 0) return 0;
        const result = totalProgress / totalWeight;

        return Math.min(Math.max(result, 0), 100);
    }, [loadingComponents]);

    const [debugMetrics, setDebugMetrics] = useState({
        searchStartTime: null,
        searchEndTime: null,

        connections: {
            'ListTable': {
                name: 'Bitacora (GPS)',
                startTime: null,
                endTime: null,
                totalRecordsExpected: 0,
                totalRecordsReceived: 0,
                totalApiCalls: 0,
                completedApiCalls: 0,
                failedApiCalls: 0,
                recoveredApiCalls: 0,
                apiCallsHistory: [],
                downloadProgress: 0,
                status: 'pending'
            }
        },

        globalTotalRecordsExpected: 0,
        globalTotalRecordsReceived: 0,
        globalDownloadProgress: 0,
        activeConnections: 0,

        componentTimes: {
            table: { start: null, end: null },
            vehicles: { start: null, end: null },
            alerts: { start: null, end: null },
            map: { start: null, end: null },
            frequency: { start: null, end: null }
        }
    });

    const updateComponentProgress = useCallback((component, progress, isComplete = false) => {
        setLoadingComponents(prevState => {
            const newState = { ...prevState };

            if (newState[component]) {
                newState[component] = {
                    ...newState[component],
                    loading: !isComplete,
                    progress: isComplete ? 100 : Math.max(0, Math.min(100, progress)),
                    complete: isComplete
                };
            }

            return newState;
        });

        // ✅ Actualizar métricas directamente sin dependencia circular
        setDebugMetrics(prev => ({
            ...prev,
            componentTimes: {
                ...prev.componentTimes,
                [component]: {
                    start: !isComplete ? Date.now() : (prev.componentTimes[component]?.start || Date.now()),
                    end: isComplete ? Date.now() : null
                }
            }
        }));
    }, []);

    const updateDebugMetrics = useCallback((updates) => {
        setDebugMetrics(prev => {
            if (!prev || !prev.connections) {
                console.warn('⚠️ debugMetrics no está inicializado correctamente');
                return prev || {
                    connections: {},
                    globalTotalRecordsExpected: 0,
                    globalTotalRecordsReceived: 0,
                    globalDownloadProgress: 0,
                    activeConnections: 0,
                    componentTimes: {}
                };
            }
            const newState = { ...prev };

            if (updates.connectionId && newState.connections[updates.connectionId]) {
                const connection = newState.connections[updates.connectionId];

                if (!connection.apiCallsHistory) {
                    connection.apiCallsHistory = [];
                }

                // Actualizar datos específicos de la conexión
                Object.keys(updates).forEach(key => {
                    if (key !== 'connectionId') {
                        if (key === 'apiCallsHistory' && Array.isArray(updates[key])) {
                            const newCalls = updates[key].map(call => ({
                                ...call,
                                timestamp: call.timestamp || Date.now()
                            }));
                            connection[key] = [...(connection[key] || []), ...newCalls];

                            if (connection[key].length > 50) {
                                connection[key] = connection[key].slice(-50);
                            }
                        } else if (key !== 'apiCallsHistory') {
                            connection[key] = updates[key];
                        }
                    }
                });

                const activeConnections = Object.values(newState.connections).filter(conn =>
                    conn && (conn.status === 'downloading' || conn.status === 'completed')
                );

                newState.globalTotalRecordsExpected = activeConnections.reduce((sum, conn) =>
                    sum + (conn.totalRecordsExpected || 0), 0
                );

                newState.globalTotalRecordsReceived = activeConnections.reduce((sum, conn) =>
                    sum + (conn.totalRecordsReceived || 0), 0
                );

                newState.globalDownloadProgress = newState.globalTotalRecordsExpected > 0 ?
                    (newState.globalTotalRecordsReceived / newState.globalTotalRecordsExpected * 100) : 0;

                newState.activeConnections = activeConnections.length;

            } else if (updates.connectionId && !newState.connections[updates.connectionId]) {
                newState.connections[updates.connectionId] = {
                    name: updates.connectionId,
                    startTime: null,
                    endTime: null,
                    totalRecordsExpected: 0,
                    totalRecordsReceived: 0,
                    totalApiCalls: 0,
                    completedApiCalls: 0,
                    failedApiCalls: 0,
                    recoveredApiCalls: 0,
                    apiCallsHistory: [],
                    downloadProgress: 0,
                    status: 'pending'
                };

                Object.keys(updates).forEach(key => {
                    if (key !== 'connectionId') {
                        newState.connections[updates.connectionId][key] = updates[key];
                    }
                });

            } else {
                Object.keys(updates).forEach(key => {
                    if (key === 'componentTimes') {
                        if (!newState.componentTimes) {
                            newState.componentTimes = {};
                        }
                        newState.componentTimes = { ...newState.componentTimes, ...updates.componentTimes };
                    } else if (key !== 'connectionId') {
                        newState[key] = updates[key];
                    }
                });
            }

            return newState;
        });

        // ✅ SINCRONIZAR DESCARGA CON BARRA DE PROGRESO
        if (updates.connectionId && updates.downloadProgress !== undefined) {
            setTimeout(() => {
                const componentMapping = {
                    'ListTable': 'list'
                };

                const componentName = componentMapping[updates.connectionId];

                if (componentName) {
                    // ✅ Usar el progreso real de descarga para actualizar el componente
                    const isComplete = updates.downloadProgress >= 100;
                    const progressValue = Math.round(updates.downloadProgress || 0);

                    console.log(`🔄 [Sincronización] ${componentName}: ${progressValue}% (descarga ${isComplete ? 'completa' : 'en progreso'})`);

                    updateComponentProgress(componentName, progressValue, isComplete);

                    // ✅ Si la descarga está completa, también marcar como cargando = false
                    if (isComplete) {
                        setLoadingComponents(prevState => ({
                            ...prevState,
                            [componentName]: {
                                loading: false,
                                progress: 100,
                                complete: true
                            }
                        }));
                    }
                }
            }, 0);
        }
    }, [updateComponentProgress]);

    const handleGenerarFiltros = (selectedFilters) => {
        setFilters(selectedFilters);
        actualizarUbicaciones([]);
        paginadorRecorridos.editarProgresoCargado(0);
        editarTotalCarga([]);
    };

    const [ubicacionInicial, actualizarUbicacionInicial] = useState([]);
    const [miUbicacion, actualizarMiUbicacion] = useState([]);
    const [ubicaciones, actualizarUbicaciones] = useState([]);
    const [distancia, actualizarDistancia] = useState(500);

    const generarFiltro = (filtro) => {
        const decoded = jwtDecode(localStorage.getItem('token'));
        return {
            fecha_inicio: filtro.fecha_inicio,
            fecha_fin: filtro.fecha_fin,
            ids_tipo_alerta: filtro.ids_tipo_alerta,
            uids_vehiculo: filtro.uids_vehiculos.map(vehiculo => vehiculo.value),
            empresaUid: decoded.uid_empresa,
            page: 1,
            limit: 10000,
            uid_usuario: decoded.uid_usuario,
            perfil: 1,
            count: true,
            tiempo: filtro.tiempo || 0
        };
    };

    useEffect(() => {
        //Metodo para Listar todas las alertas del reporte segun el tipo de reporte
        const loadPuntosCercanos = async (filtros) => {

            // Activar el spinner antes de la petición
            setIsLoading(true);

            // ✅ ESTABLECER TIEMPO DE INICIO SI NO ESTÁ YA ESTABLECIDO
            setDebugMetrics(prev => ({
                ...prev,
                searchStartTime: prev.searchStartTime || Date.now(),
                searchEndTime: null
            }));

            // ✅ MARCAR COMPONENTE COMO CARGANDO AL INICIAR DESCARGA
            setLoadingComponents(prevState => ({
                ...prevState,
                table: { loading: true, progress: 0, complete: false }
            }));

            //Iniciar progrso de carga 
            updateComponentProgress('table', 10, false);

            actualizarMiUbicacion({ ...miUbicacion, radius: Number(distancia), })
            if (paginadorRecorridos.cargando === false && filtros !== null) {
                cargarPaginas(process.env.REACT_APP_VEHICLE_LOCATIONS_URL, paginadorRecorridos, generarFiltro(filtros));
            }

            // ✅ Completar carga de tabla
            updateComponentProgress('table', 100, true);
            setIsLoading(false);

            /*
            try {
                const response = await PuntoCercanoService.getPuntosCercanos(filtros);
                if (response && response.statusCode === 200) {
                    const data = response.data.puntos_cercanos.map((punto,index) => (
                        { 
                            lat: Number(punto.lat), 
                            lng: Number(punto.long),
                            icon:iconPunto,
                            infoView: 
                            { 
                                header:<Col className="p-0 m-0 d-flex justify-items-start flex-nowrap" key={`marker_${index}`}>
                                        <div className='avatar avatar-xl me-3'>
                                        <div className={`avatar-name rounded-circle bg-info-subtle`}>
                                            <span className={`fs-9 text-info`}>
                                                {punto.noEconomico[0]}
                                            </span>
                                        </div>
                                        </div>
                                        <Flex className="align-self-center">
                                            <a className="text-800">
                                                {punto.noEconomico}
                                            </a>
                                        </Flex>
                                </Col>,
                                body:<div key={`marker_${index}`}>
                                    <Row className='m-0 p-0'>
                                        <Col className='m-0 p-0'>
                                            <Col>
                                                <div className="fw-bold">Evento:</div>
                                                <div className="fw-medium"> {punto.evento} </div>
                                            </Col>
                                            <Col>
                                                <div className="fw-bold">Fecha:</div>
                                                <div className="fw-medium">{punto.fecha}</div>
                                            </Col>
                                        </Col>
                                    </Row>
                                </div>,
                            }
                        }
                    ));
                    actualizarUbicaciones(data);
                } else {
                    toast.error(response?.message || "Error al generar reporte");
                }
            } catch (error) {
                console.error("Error al cargar puntos cercanos:", error);
            } finally {
                setIsLoading(false); // Desactivar el spinner después de la petición
            }*/
        };
        if (filters.uids_vehiculos.length > 0) {
            loadPuntosCercanos(filters);
        }
    }, [filters]);

    const obtenerDistancia = (origen, destino) => {
        const lats = origen.lat - destino.lat;
        const lngs = origen.lng - destino.lng;

        // Paso a metros
        const latm = lats * 60 * 1852;
        const lngm = (lngs * Math.cos(origen.lat * Math.PI / 180)) * 60 * 1852;

        const distInMeters = Math.sqrt(Math.pow(latm, 2) + Math.pow(lngm, 2));

        return distInMeters;
    }


    useEffect(() => {
        editarTotalCarga([{
            color: 'red',
            percent: paginadorRecorridos.progresoCargado,
            title: 'Cargando datos...',
            animated: false,
        }]);

        updateComponentProgress('table', paginadorRecorridos.progresoCargado, false);

        let datos = (Array.isArray(paginadorRecorridos.datos) ? paginadorRecorridos.datos : []);
        if (datos.length > 0) {
            let _listaPuntos = datos.filter(_punto => (_punto.tipoMotor === 1 || (_punto.tipo === 3 || _punto.tipo === 5) && _punto.tipoMotor === 0));
            var _puntosCercanos = [];
            _listaPuntos.forEach(_punto => {
                var _distancia = obtenerDistancia(miUbicacion, { lat: _punto.latitud, lng: _punto.longitud });
                const vehiculo = filters.uids_vehiculos.filter(
                    (vehiculo) => vehiculo.value === _punto.vehiculoUid
                );
                if (vehiculo.length > 0 && _distancia <= distancia) {
                    _puntosCercanos.push({
                        vehiculoId: _punto.vehiculoId,
                        noEconomico: _punto.vehiculos.noEconomico,
                        alias: _punto.vehiculos.alias,
                        fecha: dayjs(_punto.fechacliente).format('DD/MM/YYYY HH:mm:ss'),
                        hora: dayjs(_punto.fechacliente).format('HH:mm:ss'),
                        chofer: _punto.chofer?.nombreChofer || 'N/A',
                        distancia: _punto.distancia,
                        velocidad: _punto.velocidad,
                        evento: _punto.nombreEvento,
                        lat: _punto.latitud,
                        long: _punto.longitud,
                        color: vehiculo[0].color || '#000000',
                        tipo: _punto.tipo,
                    });
                }
            });
            let data = _puntosCercanos.map((punto, index) => {
                var newSVG = iconPuntoNoEconomico.url.replace('noEco', `${punto.noEconomico.length > 0 ? punto.noEconomico.slice(0, 3) : 'P'}`);
                let editMarkerIcon = { ...iconPuntoNoEconomico, url: newSVG.replaceAll('f44546', punto.color) }
                return (
                    {
                        lat: Number(punto.lat),
                        lng: Number(punto.long),
                        icon: editMarkerIcon,
                        infoView: obtenerInfoViewNormal({
                            markerId: index,
                            titulo: `${punto.noEconomico || ''} / ${punto.alias || ''}`,
                            colorFondoTitulo: `#${punto.color || '000000'}`,
                            colorTextoTitulo: 'text-white',
                            velocidad: punto.velocidad || 0,
                            alerta: punto.evento || '',
                            fecha: punto.fecha || '',
                            hora: punto.hora || '',
                            ruta: (punto.distancia ? punto.distancia.toFixed(3) : 'N/A'),
                            chofer: punto.chofer || 'N/A'
                        }),
                    }
                );
            });
            actualizarUbicaciones(data);
        }
    }, [paginadorRecorridos])

    useEffect(() => {
        actualizarMiUbicacion({ ...miUbicacion, radius: Number(distancia), })
    }, [distancia]);

    return (

        <GoogleMapsProvider>
            <div>
                <div className=" m-3 ">
                    <Row >
                        <Col md={10}>
                            <h4 className="mt-2">Radar</h4>
                        </Col>
                    </Row>
                </div>

            </div>


            <Card className="p-3 mb-3 mt-3">
                {/* <div className="d-flex justify-content-between align-items-center mb-1 mt-3">
                    <h4 className="mb-0">Buscar punto más cercano</h4>
                </div> */}

                <hr className="text-danger" />

                <PuntoCercanoFiltros
                    onGenerateReport={handleGenerarFiltros}
                    miUbicacion={miUbicacion}
                    actualizarMiUbicacion={(place) => {
                        actualizarUbicacionInicial({ ...place, radius: Number(distancia) });
                        actualizarMiUbicacion({ ...place, radius: Number(distancia) });
                    }}
                    distancia={distancia}
                    setDistancia={actualizarDistancia}
                    imprimir={paginadoRecorridosStore.cargando ? false : true}
                />

                <Col md={12}>
                    {paginadorRecorridos.cargando == false ? (<></>) : (<>

                        <Row>
                            <Col md={12}>
                                <StandardProgressBar
                                    calculateTotalProgress={calculateTotalProgress}
                                    debugMetrics={debugMetrics}
                                />
                            </Col>
                        </Row>

                        {/* <Row>
                            <Col
                                xs={6}
                                sm="auto"
                                as={Flex}
                                alignItems="center"
                                className="pe-2 small py-2"
                                key={'Generando reporte...'}
                                style={{ fontWeight: 'bold' }}
                            >
                                <span
                                    className="dot"
                                    style={{ backgroundColor: "#c00f1b", width: 10, height: 10, borderRadius: '50%', display: 'inline-block', marginRight: 5 }}
                                ></span>
                                <span>Generando reporte...</span>
                                <span className="ms-1">({`${(totalCarga.length > 0 ? totalCarga[0].percent : 0).toFixed(0)}%`})</span>
                            </Col>
                        </Row>
                        <ProgressBar
                            style={{ height: 10 }}
                            className="shadow-none rounded-4"
                            variant='success'>
                            {totalCarga.map((group, index) => (
                                <ProgressBar
                                    style={{ backgroundColor: group.color }}
                                    now={group.percent}
                                    key={group.title}
                                    animated={group.animated}
                                    className={classNames('overflow-visible position-relative', {
                                        'rounded-end rounded-pill': index === 0,
                                        'rounded-start rounded-pill': index === totalCarga.length - 1,
                                        'border-end border-100 border-0': index !== totalCarga.length - 1,
                                        'rounded-0': index !== totalCarga.length - 1 && index !== 0
                                    })}
                                />
                            ))}
                        </ProgressBar> */}
                    </>
                    )}
                </Col>
            </Card>
            <CardMap
                ubicacionInicial={ubicacionInicial}
                miUbicacion={miUbicacion}
                actualizarMiUbicacion={(ubicacionNueva) => actualizarMiUbicacion({ ...ubicacionNueva, radius: Number(distancia), })}
                ubicaciones={ubicaciones}
            />
            {isLoading &&
                // 🌀 Mostrar Spinner de carga mientras la API responde
                <div className="d-flex justify-content-center align-items-center my-5">
                    <Spinner animation="border" role="status" variant="primary" style={{ width: "3rem", height: "3rem" }}>
                        <span className="visually-hidden">Cargando...</span>
                    </Spinner>
                </div>
            }
        </GoogleMapsProvider>
    );
};

export default PuntoCercanoReport;